import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import useCheckAuthorizationStatusHandler from "../../app/checkAuthorizationStatusHandler";
import {
  generateAuthorizationCode,
  getExpAuthCodeOnClose,
  setActionPayload,
  setActionType,
  setAdditionalData,
  setAuthorizationCode,
  setOpLoading,
  setQrData,
  setStatus,
  setValidationInfo,
} from "../../features/authorizationSlice";
import { setFeatureData } from "../store/slices/appSlice";

export default function useAuthorizationQrFlow() {
  const dispatch = useDispatch();
  const store = useStore();
  const [isOpenQr, setIsOpenQr] = useState(false);
  const [shouldOpenQr, setShouldOpenQr] = useState(false);
  const { authorizationCode, loading, qrData } = useSelector(
    (state) => state.authorization,
  );

  const onCloseQr = useCallback(() => {
    setIsOpenQr(false);
  }, []);

  const onOpenQr = useCallback(() => {
    setIsOpenQr(true);
  }, []);

  const resetPendingCardReveal = useCallback(() => {
    const cardDetailState =
      store.getState()?.app?.details?.corporateCards || {};

    if (
      !cardDetailState.pendingCardDetailRequest ||
      !cardDetailState.selectedCardIdForReveal
    ) {
      return;
    }

    dispatch(
      setFeatureData({
        section: "details",
        key: "corporateCards",
        value: {
          activeRevealedCardId: null,
          cardDetailDecryptKey: null,
          cardDetailEncryptedPayloads: null,
          cardDetailError: null,
          cardDetailSocketPayload: null,
          isAwaitingCardDetails: false,
          isFetchingCardDetails: false,
          pendingCardDetailRequest: false,
          revealExpiresAt: null,
          revealedCards: {},
          selectedCardIdForReveal: null,
        },
      }),
    );
  }, [dispatch, store]);

  const onCloseQrReset = useCallback(() => {
    setShouldOpenQr(false);
    dispatch(setAuthorizationCode(""));
    dispatch(setQrData(""));
    dispatch(setStatus(""));
    dispatch(setActionType(""));
    dispatch(setActionPayload(null));
    dispatch(setValidationInfo(null));
    onCloseQr();
  }, [dispatch, onCloseQr]);

  const onOpenQrAfterGenerate = useCallback(
    (additionalInfo) => {
      setShouldOpenQr(true);
      dispatch(generateAuthorizationCode(additionalInfo));
      dispatch(setAdditionalData(additionalInfo));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!shouldOpenQr || isOpenQr) {
      return;
    }

    if (loading === "idle" && qrData) {
      onOpenQr();
      setShouldOpenQr(false);
      return;
    }

    if (loading === "idle" && !qrData) {
      setShouldOpenQr(false);
      resetPendingCardReveal();
    }
  }, [
    isOpenQr,
    loading,
    onOpenQr,
    qrData,
    resetPendingCardReveal,
    shouldOpenQr,
  ]);

  const onCloseQrWhilePending = useCallback(() => {
    setShouldOpenQr(false);
    resetPendingCardReveal();
    dispatch(setStatus("CLOSED"));
    dispatch(setOpLoading("idle"));
    dispatch(getExpAuthCodeOnClose({ authCode: authorizationCode }));
    dispatch(setValidationInfo(null));
    onCloseQrReset();
  }, [
    authorizationCode,
    dispatch,
    onCloseQrReset,
    resetPendingCardReveal,
  ]);

  useCheckAuthorizationStatusHandler(onCloseQrReset);

  return {
    isOpenQr,
    onCloseQrReset,
    onCloseQrWhilePending,
    onOpenQrAfterGenerate,
  };
}
